import * as React from 'react';
import { connect } from 'react-redux';
import { provideLocalizationService, registerForLocalization, provideIntlService, registerForIntl } from '@progress/kendo-react-intl';
import { Button } from '@progress/kendo-react-buttons';
import { Skeleton } from '@progress/kendo-react-indicators';
import { GridToolbar } from '@progress/kendo-react-grid';
import { Input } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { OverviewPage, StackLayout, Text } from '../../../ui';
import { ErrorMessage } from '../../../ui/components/common';
import { DateTimeFormatter, EmptyFormatter, Grid, ValueItem } from '../../shared/components';
import {
  loadSessionOverviewPage,
  openSessionOverviewConfirmDialog,
  closeSessionOverviewConfirmDialog,
  removeSessionOverviewConfirmDialog,
  loadSessionParticipants,
  setParticipantsKeyword,
  sortParticipants,
  toggleAddParticipantDialog,
  openRemoveParticipantDialog,
  closeRemoveParticipantDialog,
  removeParticipant,
  cleanupSessionOverviewPage
} from '../actions/session-overview-page-actions';
import { GeneralCompetencyEventSessionQrDialog } from './';
import SessionAddParticipantDialog from './session-add-participant-dialog';
import { withRouter } from '../../../components/withRouter';
import { GENERAL_COMPETENCY_EVENT_STATUSES } from '../../../resources/generalCompetencyEventStatus';

const PARTICIPANTS_SEARCH_DEBOUNCE_MS = 300;

class GeneralCompetencyEventSessionOverviewPage extends React.PureComponent {

  constructor() {
    super();
    this.state = { isQrDialogVisible: false };
    this.searchDebounceTimer = null;
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
    this.handleOpenQrDialog = this.handleOpenQrDialog.bind(this);
    this.handleCloseQrDialog = this.handleCloseQrDialog.bind(this);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.handleConfirmDialogClose = this.handleConfirmDialogClose.bind(this);
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
    this.handleAddParticipantClick = this.handleAddParticipantClick.bind(this);
    this.handleRemoveParticipantClick = this.handleRemoveParticipantClick.bind(this);
    this.handleRemoveParticipantDialogClose = this.handleRemoveParticipantDialogClose.bind(this);
    this.handleRemoveParticipantConfirm = this.handleRemoveParticipantConfirm.bind(this);
    this.handleParticipantsKeywordChange = this.handleParticipantsKeywordChange.bind(this);
    this.handleParticipantsPageChange = this.handleParticipantsPageChange.bind(this);
    this.handleParticipantsSortChange = this.handleParticipantsSortChange.bind(this);
  }

  async componentDidMount() {
    if (this.props.params?.generalCompetencyEventId && this.props.params?.sessionId) {
      this.props.loadSessionOverviewPage({
        eventId: this.props.params.generalCompetencyEventId,
        sessionId: this.props.params.sessionId
      });
      this.props.loadSessionParticipants({
        eventId: this.props.params.generalCompetencyEventId,
        sessionId: this.props.params.sessionId
      });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.searchDebounceTimer);
    this.props.cleanupSessionOverviewPage();
  }

  render() {
    const { event, session, loading, canWrite, userIsAdministrator } = this.props;
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    const canEditParticipants = canWrite
      && event?.status !== GENERAL_COMPETENCY_EVENT_STATUSES.CANCELLED
      && (event?.status !== GENERAL_COMPETENCY_EVENT_STATUSES.COMPLETED || userIsAdministrator);
    return (
      <>
        <OverviewPage
          title={loading
            ? <Skeleton shape='text' style={{ width: 300, height: 28 }}/>
            : EmptyFormatter(session?.name)
          }
          headerActions={
            <>
              <Button
                type='button'
                disabled={event?.status === GENERAL_COMPETENCY_EVENT_STATUSES.COMPLETED || event?.status === GENERAL_COMPETENCY_EVENT_STATUSES.CANCELLED}
                onClick={this.handleOpenQrDialog}
              >
                {localizationService.toLanguageString('generalCompetencyEventSessionQr.openDialog')}
              </Button>
              {canWrite &&
                <>
                  <Button
                    id='edit-button'
                    type='button'
                    themeColor='primary'
                    disabled={event?.status !== GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT}
                    onClick={this.handleEditClick}
                  >
                    {localizationService.toLanguageString('custom.edit')}
                  </Button>
                  <Button
                    type='button'
                    disabled={event?.status !== GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT || session?.hasParticipants}
                    onClick={this.handleDeleteClick}
                  >
                    {localizationService.toLanguageString('buttons.delete')}
                  </Button>
                </>
              }
            </>
          }
          breadcrumbItems={[
            {
              id: 'general_competency_events',
              text: localizationService.toLanguageString('generalCompetencyEvent.listTitle')
            },
            {
              id: 'general_competency_event_overview',
              text: localizationService.toLanguageString('generalCompetencyEvent.overviewTitle')
            },
            {
              id: 'session_overview',
              text: localizationService.toLanguageString('generalCompetencyEventSessionOverview.overviewTitle'),
              disabled: true
            }
          ]}
          onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
          onBackClick={this.handleBackClick}
          summary={
            <StackLayout rowGap='8px'>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEventSession.name')}
                labelWidth='170px'
                loading={loading}
              >
                {EmptyFormatter(session?.name)}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEventSession.description')}
                labelWidth='170px'
                loading={loading}
              >
                <Text variant={'body2'} overflowWrap={true}>
                  {EmptyFormatter(session?.description)}
                </Text>
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEventSession.lecturerName')}
                labelWidth='170px'
                loading={loading}
              >
                {EmptyFormatter(session?.lecturerName)}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEventSession.startAt')}
                labelWidth='170px'
                loading={loading}
              >
                {session?.startAt ? DateTimeFormatter(session.startAt, intlService) : EmptyFormatter(null)}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEventSession.durationMinutes')}
                labelWidth='170px'
                loading={loading}
              >
                {EmptyFormatter(session?.durationMinutes)}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEventSession.location')}
                labelWidth='170px'
                loading={loading}
              >
                {EmptyFormatter(session?.location)}
              </ValueItem>
            </StackLayout>
          }
        >
          <Grid
            loading={this.props.participantsLoading}
            data={this.props.participants}
            skip={this.props.participantsSkip}
            take={this.props.participantsTake}
            total={this.props.participantsTotal}
            sort={this.props.participantsSorting}
            onPageChange={this.handleParticipantsPageChange}
            onSortChange={this.handleParticipantsSortChange}
            columns={[
              {
                field: 'studentName',
                className: 'align-left',
                headerClassName: 'align-left',
                title: localizationService.toLanguageString('generalCompetencyEventSessionOverview.participantName')
              },
              {
                field: 'status',
                className: 'align-left',
                headerClassName: 'align-left',
                title: localizationService.toLanguageString('generalCompetencyEventSessionOverview.participantStatus'),
                sortable: false
              },
              {
                field: 'occurredAt',
                className: 'align-left',
                headerClassName: 'align-left',
                title: localizationService.toLanguageString('generalCompetencyEventSessionOverview.participantOccurredAt'),
                cell: (props) => (
                  <td>{EmptyFormatter(DateTimeFormatter(props.dataItem.occurredAt, intlService))}</td>
                )
              }
            ]}
            actions={
              canEditParticipants && [{
                icon: 'delete',
                onClick: this.handleRemoveParticipantClick
              }]
            }
          >
            <GridToolbar>
              {canEditParticipants &&
                <Button themeColor='primary' onClick={this.handleAddParticipantClick}>
                  {localizationService.toLanguageString('generalCompetencyEventSessionOverview.addParticipant')}
                </Button>
              }
              <Input
                value={this.props.participantsKeyword}
                onChange={this.handleParticipantsKeywordChange}
                placeholder={localizationService.toLanguageString('generalCompetencyEventSessionOverview.searchParticipantPlaceholder')}
                style={{ width: '340px', marginLeft: 'auto' }}
              />
            </GridToolbar>
          </Grid>
        </OverviewPage>
        {this.state.isQrDialogVisible &&
          <GeneralCompetencyEventSessionQrDialog
            eventId={this.props.params.generalCompetencyEventId}
            sessionId={this.props.params.sessionId}
            session={session}
            eventStatus={event?.status}
            canWrite={canWrite}
            onClose={this.handleCloseQrDialog}
          />
        }
        {this.props.isAddParticipantDialogVisible &&
          <SessionAddParticipantDialog
            eventId={this.props.params.generalCompetencyEventId}
            sessionId={this.props.params.sessionId}
          />
        }
        {this.props.isRemoveParticipantDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.handleRemoveParticipantDialogClose}>
            <Text>{localizationService.toLanguageString('generalCompetencyEventSessionOverview.removeParticipantConfirm')}</Text>
            <DialogActionsBar>
              <Button themeColor={'primary'} onClick={this.handleRemoveParticipantConfirm}>{localizationService.toLanguageString('buttons.delete')}</Button>
              <Button onClick={this.handleRemoveParticipantDialogClose}>{localizationService.toLanguageString('buttons.cancel')}</Button>
            </DialogActionsBar>
          </Dialog>
        }
        {this.props.isConfirmDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.handleConfirmDialogClose}>
            {this.props.confirmDialogErrorMessage &&
              <ErrorMessage>{this.props.confirmDialogErrorMessage}</ErrorMessage>
            }
            <Text>{localizationService.toLanguageString('generalCompetencyEventSession.deleteConfirmDialogMessage')}</Text>
            <DialogActionsBar>
              <Button themeColor={'primary'} onClick={this.handleDeleteConfirm}>{localizationService.toLanguageString('buttons.delete')}</Button>
              <Button onClick={this.handleConfirmDialogClose}>{localizationService.toLanguageString('buttons.cancel')}</Button>
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  handleEditClick() {
    this.props.navigate(`/general_competency_events/${this.props.params.generalCompetencyEventId}/sessions/${this.props.params.sessionId}`);
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'general_competency_events') {
      this.props.navigate('/general_competency_events');
    } else if (e.id === 'general_competency_event_overview') {
      this.props.navigate(`/general_competency_events/${this.props.params.generalCompetencyEventId}/overview`);
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleOpenQrDialog() {
    this.setState({ isQrDialogVisible: true });
  }

  handleCloseQrDialog() {
    this.setState({ isQrDialogVisible: false });
  }

  handleDeleteClick() {
    this.props.openSessionOverviewConfirmDialog();
  }

  handleConfirmDialogClose() {
    this.props.closeSessionOverviewConfirmDialog();
  }

  handleDeleteConfirm() {
    this.props.removeSessionOverviewConfirmDialog({
      eventId: this.props.params.generalCompetencyEventId,
      sessionId: this.props.params.sessionId,
      navigate: this.props.navigate
    });
  }

  handleAddParticipantClick() {
    this.props.toggleAddParticipantDialog(true);
  }

  handleRemoveParticipantClick(row) {
    this.props.openRemoveParticipantDialog(row.dataItem);
  }

  handleRemoveParticipantDialogClose() {
    this.props.closeRemoveParticipantDialog();
  }

  handleRemoveParticipantConfirm() {
    this.props.removeParticipant({
      eventId: this.props.params.generalCompetencyEventId,
      sessionId: this.props.params.sessionId
    });
  }

  handleParticipantsKeywordChange(e) {
    this.props.setParticipantsKeyword(e.value ?? '');
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.props.loadSessionParticipants({
        eventId: this.props.params.generalCompetencyEventId,
        sessionId: this.props.params.sessionId
      });
    }, PARTICIPANTS_SEARCH_DEBOUNCE_MS);
  }

  handleParticipantsPageChange(e) {
    this.props.loadSessionParticipants({
      eventId: this.props.params.generalCompetencyEventId,
      sessionId: this.props.params.sessionId,
      skip: e.page.skip,
      take: e.page.take
    });
  }

  handleParticipantsSortChange(e) {
    this.props.sortParticipants(e.sort);
    this.props.loadSessionParticipants({
      eventId: this.props.params.generalCompetencyEventId,
      sessionId: this.props.params.sessionId
    });
  }
}

registerForLocalization(GeneralCompetencyEventSessionOverviewPage);
registerForIntl(GeneralCompetencyEventSessionOverviewPage);

const mapStateToProps = state => ({
  event: state.generalCompetencyEventSessionOverviewPage.event,
  session: state.generalCompetencyEventSessionOverviewPage.session,
  loading: state.generalCompetencyEventSessionOverviewPage.loading,
  canWrite: state.generalCompetencyEventSessionOverviewPage.canWrite,
  isConfirmDialogVisible: state.generalCompetencyEventSessionOverviewPage.isConfirmDialogVisible,
  confirmDialogErrorMessage: state.generalCompetencyEventSessionOverviewPage.confirmDialogErrorMessage,
  userIsAdministrator: state.app.userIsAdministrator,
  participants: state.generalCompetencyEventSessionOverviewPage.participants,
  participantsLoading: state.generalCompetencyEventSessionOverviewPage.participantsLoading,
  participantsKeyword: state.generalCompetencyEventSessionOverviewPage.participantsKeyword,
  participantsSkip: state.generalCompetencyEventSessionOverviewPage.participantsSkip,
  participantsTake: state.generalCompetencyEventSessionOverviewPage.participantsTake,
  participantsTotal: state.generalCompetencyEventSessionOverviewPage.participantsTotal,
  participantsSorting: state.generalCompetencyEventSessionOverviewPage.participantsSorting,
  isAddParticipantDialogVisible: state.generalCompetencyEventSessionOverviewPage.isAddParticipantDialogVisible,
  isRemoveParticipantDialogVisible: state.generalCompetencyEventSessionOverviewPage.isRemoveParticipantDialogVisible
});

const mapDispatchToProps = dispatch => ({
  loadSessionOverviewPage: (payload) => dispatch(loadSessionOverviewPage(payload)),
  openSessionOverviewConfirmDialog: () => dispatch(openSessionOverviewConfirmDialog()),
  closeSessionOverviewConfirmDialog: () => dispatch(closeSessionOverviewConfirmDialog()),
  removeSessionOverviewConfirmDialog: (payload) => dispatch(removeSessionOverviewConfirmDialog(payload)),
  loadSessionParticipants: (payload) => dispatch(loadSessionParticipants(payload)),
  setParticipantsKeyword: (keyword) => dispatch(setParticipantsKeyword(keyword)),
  sortParticipants: (sorting) => dispatch(sortParticipants(sorting)),
  toggleAddParticipantDialog: (isVisible) => dispatch(toggleAddParticipantDialog(isVisible)),
  openRemoveParticipantDialog: (payload) => dispatch(openRemoveParticipantDialog(payload)),
  closeRemoveParticipantDialog: () => dispatch(closeRemoveParticipantDialog()),
  removeParticipant: (payload) => dispatch(removeParticipant(payload)),
  cleanupSessionOverviewPage: () => dispatch(cleanupSessionOverviewPage())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(GeneralCompetencyEventSessionOverviewPage));
